import React from 'react';
import { InternalSearchMetaDataType } from '../../../../types/InternalSearchMetaDataType';

export enum InternalSearchCardType {
  Simple = 'Simple',
  Article = 'Article',
  CategoryApp = 'CategoryApp',
  Product = 'Product',
  RouteSuggestion = 'RouteSuggestion',
  Car = 'Car',
  BecomeAMember = 'BecomeAMember',
  Benefit = 'Benefit',
  LocalDepartment = 'LocalDepartment',
  LocalBenefit = 'LocalBenefit',
  LocalArticle = 'LocalArticle',
  Event = 'Event',
  PracticeTrack = 'PracticeTrack',
  NAFCenter = 'NAFCenter',
  Placeholder = 'Placeholder',
  NafCenterProduct = 'NafCenterProduct',
}

export enum ElasticFieldNames {
  nafPageType = 'naf_page_type',
  nafPageTitle = 'naf_page_title',
  nafPageDescription = 'naf_page_description',
  nafImagePublicId = 'naf_image_publicid',
  productTitle = 'product_title',
  author = 'author',
  tag = 'tag',
  brandName = 'brand_name',
  carModelImage = 'car_model_image',
  partnerLogoUrl = 'partner_logo_url',
  partnerLogoPublicId = 'partner_logo_public_id',
  partnerName = 'partner_name',
  nafImageUrl = 'naf_image_url',
  organizerTitle = 'organizer_title',
  streetAddress = 'street_address',
  postalCode = 'postal_code',
  contactPhone = 'contact_phone',
  contactEMail = 'contact_email',
}

const elasticMeta = (field: ElasticFieldNames, content?: string | null) =>
  content ? <meta className="elastic" key={field} name={field} content={content} /> : null;

export const InternalSearchMetaData = ({
  internalSearchMetaData,
  title,
  description,
}: {
  internalSearchMetaData: InternalSearchMetaDataType;
  title: string;
  description: string;
}) => {
  // This component returns an array instead of a fragment, due to lack of support of fragments in React Helmet
  // https://github.com/nfl/react-helmet/issues/342
  const fields = [
    elasticMeta(ElasticFieldNames.nafPageType, internalSearchMetaData.cardType),
    elasticMeta(ElasticFieldNames.nafPageTitle, title),
    elasticMeta(ElasticFieldNames.nafPageDescription, description),
    elasticMeta(ElasticFieldNames.nafImagePublicId, internalSearchMetaData?.imagePublicId),
  ];

  if (internalSearchMetaData.cardType === InternalSearchCardType.Product) {
    const { product } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.productTitle, product?.title));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.Article) {
    const { author, tags } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.author, author));
    tags?.forEach((tag) => fields.push(elasticMeta(ElasticFieldNames.tag, tag)));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.Car) {
    const { brandName, carModelImage } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.brandName, brandName));
    fields.push(elasticMeta(ElasticFieldNames.carModelImage, carModelImage));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.Benefit) {
    const { partnerLogoPublicId, partnerName } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.partnerLogoPublicId, partnerLogoPublicId));
    fields.push(elasticMeta(ElasticFieldNames.partnerName, partnerName));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.LocalDepartment) {
    const { imageUrl } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.nafImageUrl, imageUrl));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.LocalBenefit) {
    const { imageUrl, partnerName, partnerLogoUrl } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.nafImageUrl, imageUrl));
    fields.push(elasticMeta(ElasticFieldNames.partnerName, partnerName));
    fields.push(elasticMeta(ElasticFieldNames.partnerLogoUrl, partnerLogoUrl));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.LocalArticle) {
    const { author, tags, imageUrl } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.author, author));
    tags?.forEach((tag) => fields.push(elasticMeta(ElasticFieldNames.tag, tag)));
    fields.push(elasticMeta(ElasticFieldNames.nafImageUrl, imageUrl));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.Event) {
    const { imageUrl, organizerTitle } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.organizerTitle, organizerTitle));
    fields.push(elasticMeta(ElasticFieldNames.nafImageUrl, imageUrl));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.PracticeTrack) {
    const { streetAddress, postalCode, contactPhone, contactEMail } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.streetAddress, streetAddress));
    fields.push(elasticMeta(ElasticFieldNames.postalCode, postalCode));
    fields.push(elasticMeta(ElasticFieldNames.contactPhone, contactPhone));
    fields.push(elasticMeta(ElasticFieldNames.contactEMail, contactEMail));
  }
  if (internalSearchMetaData.cardType === InternalSearchCardType.NAFCenter) {
    const { streetAddress, postalCode, imagePublicId } = internalSearchMetaData;
    fields.push(elasticMeta(ElasticFieldNames.streetAddress, streetAddress));
    fields.push(elasticMeta(ElasticFieldNames.postalCode, postalCode));
    fields.push(elasticMeta(ElasticFieldNames.nafImagePublicId, imagePublicId));
  }

  return fields.filter(Boolean);
};

export const resultFields = Object.values(ElasticFieldNames).reduce(
  (acc, cur) => {
    acc[cur] = {
      raw: {},
      snippet: {},
    };
    return acc;
  },
  {
    url: { raw: {} },
    title: { raw: {} },
    body_content: { raw: {}, snippet: {} },
    meta_description: { raw: {} },
    id: { raw: {} },
  },
);
